import { Stack } from '@mui/material';
import { useMemo } from 'react';
import { useFormContext, useFormState } from 'react-hook-form';

import { TextInput } from '@/components/Form/TextInput';

import { TIN_LENGTH } from '@/constants/common';
import { TIN_PATTERN } from '@/constants/patterns';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectConfigsState } from '@/redux/configs';

import { CountryISO } from '@/types/places';
import { User } from '@/types/user';

import { getOtherTaxCountries, shouldShowTinForCountry } from '@/utils/user';

import { UpdatePersonalInfo } from '../types';

type SSNTINFieldsProps = {
  user: User;
  differentTaxResidency: boolean;
};

export const SSNTINFields = ({
  user,
  differentTaxResidency,
}: SSNTINFieldsProps) => {
  const { watch, control } = useFormContext<UpdatePersonalInfo>();
  const { errors: visibleErrors } = useFormState<UpdatePersonalInfo>({
    control,
  });
  const { requiredLocalTIN, isoCode: localISO } =
    useAppSelector(selectConfigsState);
  const { t, country: translatedCountry } = useTranslate();

  const taxResidences = watch('taxResidences');
  const country = watch('addressDetailed.country');

  const { otherCountriesTin, shouldShowLocalTin, shouldShowUsTin } =
    useMemo(() => {
      const selectedTaxCountries = (
        differentTaxResidency ? taxResidences : [country]
      ).filter(Boolean);
      const shouldShowLocalTin =
        requiredLocalTIN &&
        shouldShowTinForCountry(
          user,
          selectedTaxCountries,
          localISO as CountryISO,
        );
      return {
        shouldShowUsTin: shouldShowTinForCountry(
          user,
          selectedTaxCountries,
          CountryISO.US,
        ),
        shouldShowLocalTin,
        otherCountriesTin: shouldShowLocalTin
          ? getOtherTaxCountries(selectedTaxCountries, [
              CountryISO.US,
              localISO,
            ])
          : getOtherTaxCountries(selectedTaxCountries, [CountryISO.US]),
      };
    }, [
      country,
      differentTaxResidency,
      localISO,
      requiredLocalTIN,
      taxResidences,
      user,
    ]);
  return (
    <>
      {shouldShowLocalTin && (
        <Stack direction="column" alignItems="stretch" gap={3}>
          <TextInput
            inputMode="numeric"
            name="localTin"
            control={control}
            error={!!visibleErrors.localTin}
            helperText={visibleErrors.localTin?.message}
            label={t('updatePersonalInfo.personalInfoForm.field.tin.label', {
              value: translatedCountry[localISO],
            })}
            rules={{
              required: t(
                'updatePersonalInfo.personalInfoForm.field.tin.error.required',
                { value: translatedCountry[localISO] },
              ),
              pattern: {
                value: new RegExp(TIN_PATTERN),
                message: t(
                  'updatePersonalInfo.personalInfoForm.field.tin.error.pattern',
                ),
              },
              maxLength: {
                value: TIN_LENGTH.otherMax,
                message: t(
                  'updatePersonalInfo.personalInfoForm.field.tin.error.maximum',
                  { value: `${TIN_LENGTH.otherMax}` },
                ),
              },
              minLength: {
                value: TIN_LENGTH.otherMin,
                message: t(
                  'updatePersonalInfo.personalInfoForm.field.tin.error.minimum',
                  { value: `${TIN_LENGTH.otherMin}` },
                ),
              },
            }}
          />
        </Stack>
      )}
      {shouldShowUsTin && (
        <Stack direction="column" alignItems="stretch" gap={3}>
          <TextInput
            inputMode="numeric"
            name="usTin"
            label={t('updatePersonalInfo.personalInfoForm.field.tin.label', {
              value: translatedCountry[CountryISO.US],
            })}
            control={control}
            error={!!visibleErrors.usTin}
            helperText={visibleErrors.usTin?.message}
            rules={{
              pattern: {
                value: new RegExp(TIN_PATTERN),
                message: t(
                  'updatePersonalInfo.personalInfoForm.field.tin.error.pattern',
                ),
              },
              maxLength: {
                value: TIN_LENGTH.usMax,
                message: t(
                  'updatePersonalInfo.personalInfoForm.field.tin.error.maximum',
                  { value: `${TIN_LENGTH.usMax}` },
                ),
              },
              minLength: {
                value: TIN_LENGTH.usMin,
                message: t(
                  'updatePersonalInfo.personalInfoForm.field.tin.error.minimum',
                  { value: `${TIN_LENGTH.usMin}` },
                ),
              },
            }}
          />
        </Stack>
      )}
      {otherCountriesTin?.length > 0 && (
        <Stack direction="column" alignItems="stretch" gap={3}>
          {otherCountriesTin.map(countryCode => (
            <TextInput
              key={countryCode}
              inputMode="numeric"
              name={`otherTins.${countryCode}`}
              label={t('updatePersonalInfo.personalInfoForm.field.tin.label', {
                value: translatedCountry[countryCode],
              })}
              control={control}
              error={!!visibleErrors?.otherTins?.[countryCode]}
              helperText={visibleErrors?.otherTins?.[countryCode]?.message}
              rules={{
                pattern: {
                  value: new RegExp(TIN_PATTERN),
                  message: t(
                    'updatePersonalInfo.personalInfoForm.field.tin.error.pattern',
                  ),
                },
                maxLength: {
                  value: TIN_LENGTH.otherMax,
                  message: t(
                    'updatePersonalInfo.personalInfoForm.field.tin.error.maximum',
                    { value: `${TIN_LENGTH.otherMax}` },
                  ),
                },
                minLength: {
                  value: TIN_LENGTH.otherMin,
                  message: t(
                    'updatePersonalInfo.personalInfoForm.field.tin.error.minimum',
                    { value: `${TIN_LENGTH.otherMin}` },
                  ),
                },
              }}
            />
          ))}
        </Stack>
      )}
    </>
  );
};
